/**
 * Clerk email-OTP implementation of OtpProvider (managed — Clerk sends the
 * emails; free tier covers 50k monthly retained users).
 *
 * Flow (headless @clerk/clerk-js, no Clerk UI components):
 *   1. sendOtp   → signIn.create({strategy:'email_code', identifier,
 *                  signUpIfMissing:true}) — ONE combined flow for both new and
 *                  returning dealers; Clerk emails the 6-digit code.
 *   2. verifyOtp → signIn.attemptFirstFactor({strategy:'email_code', code})
 *                  → setActive(session) → session.getToken() (Clerk session JWT)
 *   3. EXCHANGE  → POST the Clerk JWT to the sync Worker endpoint
 *                  ({action:'clerk_exchange'} — see workers/sync/src/clerk.ts),
 *                  which JWKS-verifies it, fetches the verified email via the
 *                  Clerk Backend API, and mints a Firebase CUSTOM TOKEN
 *                  (uid = Clerk user id, claims {email})
 *                  → signInWithCustomToken().
 *
 * Why the exchange: Firestore security rules only trust Firebase Auth, and
 * Clerk's native Firebase integration is closed to new applications (2026), so
 * the bridge runs on the anyway-sync Cloudflare Worker.
 * The app's real session is the FIREBASE session — we sign out of Clerk right
 * after the exchange, so repeat logins never hit a stale Clerk session.
 *
 * Emulator mode (VITE_USE_EMULATORS=true): short-circuits via emulatorAuth.ts —
 * any email works with the fixed code 123456 (no Clerk instance needed).
 */
import { Clerk } from '@clerk/clerk-js';
import { signInWithCustomToken, type UserCredential } from 'firebase/auth';
import { EMAIL_OTP_CODE_LENGTH } from '@mitang/shared';
import { auth } from '../firebase';
import { clerkPublishableKey, useEmulators } from '../env';
import { callOtpEndpoint, friendlyOtpError, isValidEmail } from './emailOtpProvider';
import { EMULATOR_DEV_CODE, emulatorVerifyAndSignIn } from './emulatorAuth';
import type { OtpConfirmation, OtpProvider } from './types';

interface ClerkConfirmation extends OtpConfirmation {
  readonly provider: 'clerk';
}

/**
 * Container Clerk's smart bot-protection can render its (invisible) challenge
 * into during sign-up flows. Must exist before signIn.create with
 * signUpIfMissing, or bot-protected instances reject the transfer.
 */
const CLERK_CAPTCHA_CONTAINER_ID = 'clerk-captcha';

let clerkInstance: Clerk | null = null;
let clerkLoad: Promise<Clerk> | null = null;

function ensureCaptchaContainer(): void {
  if (!document.getElementById(CLERK_CAPTCHA_CONTAINER_ID)) {
    const el = document.createElement('div');
    el.id = CLERK_CAPTCHA_CONTAINER_ID;
    document.body.appendChild(el);
  }
}

async function getClerk(): Promise<Clerk> {
  if (!clerkPublishableKey) {
    throw new Error(
      'Clerk is not configured (VITE_CLERK_PUBLISHABLE_KEY). Create a Clerk app and set its publishable key.',
    );
  }
  if (clerkInstance?.loaded) return clerkInstance;
  if (!clerkLoad) {
    clerkLoad = (async () => {
      ensureCaptchaContainer();
      const clerk = new Clerk(clerkPublishableKey);
      await clerk.load();
      clerkInstance = clerk;
      return clerk;
    })().catch((err: unknown) => {
      clerkLoad = null; // allow retry after a failed load (offline etc.)
      throw err;
    });
  }
  return clerkLoad;
}

/** Clerk API errors carry {errors: [{message, longMessage}]} — surface nicely. */
function clerkErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'errors' in err) {
    const first = (err as { errors: Array<{ message?: string; longMessage?: string }> })
      .errors?.[0];
    const msg = first?.longMessage || first?.message;
    if (msg) return msg;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export const clerkOtpProvider: OtpProvider = {
  name: 'clerk',

  async sendOtp(emailRaw: string): Promise<OtpConfirmation> {
    const email = emailRaw.trim().toLowerCase();
    if (!isValidEmail(email)) throw new Error('Enter a valid email address.');

    if (useEmulators) {
      // eslint-disable-next-line no-console
      console.info(`[clerkOtp] EMULATOR mode — use code ${EMULATOR_DEV_CODE} for ${email}`);
    } else {
      const clerk = await getClerk();
      if (!clerk.client) throw new Error('Sign-in service failed to load. Please try again.');
      // A lingering Clerk session (e.g. an aborted earlier login) would make
      // signIn.create fail with session_exists — clear it first.
      if (clerk.session) await clerk.signOut();
      try {
        const signIn = await clerk.client.signIn.create({
          strategy: 'email_code',
          identifier: email,
          signUpIfMissing: true,
        });
        // A brand-new email TRANSFERS the combined flow to a SIGN-UP: the code
        // then belongs to the signUp object, not the signIn (verifyOtp handles
        // both). Make sure its email verification was actually prepared/sent.
        if (signIn.status !== 'needs_first_factor') {
          const signUp = clerk.client.signUp;
          if (signUp.status !== 'missing_requirements') {
            throw new Error('Could not start sign-in. Please try again.');
          }
          if (signUp.verifications.emailAddress.status !== 'unverified') {
            await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
          }
        }
      } catch (err) {
        throw new Error(clerkErrorMessage(err, 'Could not send the code. Please try again.'));
      }
    }
    const confirmation: ClerkConfirmation = { destination: email, provider: 'clerk' };
    return confirmation;
  },

  async verifyOtp(confirmation: OtpConfirmation, code: string): Promise<UserCredential> {
    if (confirmation.provider !== 'clerk') {
      throw new Error('Invalid confirmation handle for clerk provider.');
    }
    const email = confirmation.destination;
    const trimmed = code.trim();
    if (trimmed.length !== EMAIL_OTP_CODE_LENGTH) {
      throw new Error('Invalid code. Check the latest email and try again.');
    }

    if (useEmulators) {
      return emulatorVerifyAndSignIn(email, trimmed);
    }

    const clerk = await getClerk();
    if (!clerk.client) throw new Error('Your sign-in expired. Please request a new code.');

    // 1. Verify the code with Clerk — against whichever flow is active. A
    //    first-time email lives on the SIGN-UP object (combined-flow transfer);
    //    a returning dealer lives on the SIGN-IN object.
    const signUp = clerk.client.signUp;
    const isSignUpFlow =
      signUp.status === 'missing_requirements' &&
      (signUp.emailAddress ?? '').toLowerCase() === email;

    let sessionId: string | null = null;
    try {
      if (isSignUpFlow) {
        const result = await signUp.attemptEmailAddressVerification({ code: trimmed });
        if (result.status !== 'complete' || !result.createdSessionId) {
          throw new Error('Invalid code. Check the latest email and try again.');
        }
        sessionId = result.createdSessionId;
      } else {
        const signIn = clerk.client.signIn;
        if (signIn.status === null) {
          throw new Error('Your sign-in expired. Please request a new code.');
        }
        let needsTransfer = false;
        try {
          const result = await signIn.attemptFirstFactor({ strategy: 'email_code', code: trimmed });
          if (result.status === 'complete' && result.createdSessionId) {
            sessionId = result.createdSessionId;
          } else if (result.firstFactorVerification.status === 'transferable') {
            needsTransfer = true;
          } else {
            throw new Error('Invalid code. Check the latest email and try again.');
          }
        } catch (err) {
          // Brand-new email + CORRECT code: Clerk marks the verification
          // 'transferable' and rejects the sign-in — the account is then
          // created by transferring to a sign-up (no second code needed).
          if (clerk.client.signIn.firstFactorVerification.status === 'transferable') {
            needsTransfer = true;
          } else {
            throw err;
          }
        }
        if (needsTransfer) {
          const created = await clerk.client.signUp.create({ transfer: true });
          if (created.status !== 'complete' || !created.createdSessionId) {
            throw new Error('Could not create your account. Please try again.');
          }
          sessionId = created.createdSessionId;
        }
      }
    } catch (err) {
      throw new Error(clerkErrorMessage(err, 'Invalid code. Check the latest email and try again.'));
    }
    if (!sessionId) throw new Error('Invalid code. Check the latest email and try again.');

    // 2. Activate the Clerk session and grab its short-lived session JWT.
    await clerk.setActive({ session: sessionId });
    const clerkJwt = await clerk.session?.getToken();
    if (!clerkJwt) throw new Error('Sign-in could not be completed. Please try again.');

    // 3. Exchange it for a Firebase custom token via the Apps Script bridge.
    const res = await callOtpEndpoint({ action: 'clerk_exchange', token: clerkJwt });
    if (!res.ok) throw new Error(friendlyOtpError(res));
    if (!res.token) throw new Error('Sign-in service returned no token. Please try again.');
    const credential = await signInWithCustomToken(auth, res.token);

    // 4. The Firebase session is the app's real session — drop the Clerk one
    //    (best-effort) so future logins start clean.
    void clerk.signOut().catch(() => undefined);

    return credential;
  },
};
